import { Controller } from "@hotwired/stimulus";
import { resetOnBfcacheRestore } from "../app/helpers/bfcache";
import { translate } from "../app/helpers/classes/translation";

/* global gon */

// Orchestrates step3's submit flow: validate (package chosen if in package
// mode, every addon yes/no answered, personal_number/address filled where
// required and visible) -> hide the overlay/enable buttons once a
// selection is made -> on real submit, kick off the async
// orderlines-finalization poll (see verify_orderlines.js.erb) and only
// let the browser actually POST once that resolves successfully.
//
// Note on the personal-number/address validation: the original
// (personal_number.ts / address_block.ts) had a real bug -- its "regex"
// checks were `!regex.test` (comparing the RegExp object itself to falsy,
// which is always false) instead of `!regex.test(value)`, so those fields
// were effectively only ever checked for non-blank, never actually
// pattern-validated. Reimplemented here with real regex checks instead of
// silently carrying the bug forward.
export default class extends Controller {
  static targets = ["packageOverlay", "buttons", "spinner", "submitButton", "errorBanner"];
  static values = { verifyUrl: String };

  connect() {
    this.bypassValidation = false;
    this.boundSuccess = () => this.onVerifySuccess();
    this.boundFailed = () => this.onVerifyFailed();
    document.addEventListener("step3:verify-success", this.boundSuccess);
    document.addEventListener("step3:verify-failed", this.boundFailed);
    this.teardownBfcache = resetOnBfcacheRestore(() => this.resetSubmitting());
  }

  disconnect() {
    document.removeEventListener("step3:verify-success", this.boundSuccess);
    document.removeEventListener("step3:verify-failed", this.boundFailed);
    if (this.teardownBfcache) this.teardownBfcache();
  }

  // See app/helpers/bfcache.js -- Back from step4 otherwise returns to a
  // spinner where "Gå till betalning" used to be.
  resetSubmitting() {
    this.bypassValidation = false;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden");
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.remove("hidden");
  }

  // A package/addon/upgrade selection was made -- the "must choose a
  // package first" overlay (if any) and the initially-hidden button row
  // (when a VIP upgrade choice hasn't been made yet) can come off now.
  clearBlockers() {
    if (this.hasPackageOverlayTarget) this.packageOverlayTarget.classList.add("hidden");
    this.buttonsTarget.classList.remove("hidden");
    this.buttonsTarget.style.display = "";
  }

  submit(event) {
    if (this.bypassValidation) {
      this.bypassValidation = false;
      return;
    }

    event.preventDefault();
    this.hideError();

    const errors = this.validate();
    if (errors.length > 0) {
      this.showError(errors[0]);
      return;
    }

    this.submitButtonTarget.classList.add("hidden");
    this.spinnerTarget.classList.remove("hidden");
    this.startVerification();
  }

  validate() {
    const errors = [];

    const packageRadios = this.element.querySelectorAll('input[name="package"]');
    if (packageRadios.length > 0 && this.element.querySelector('input[name="package"]:checked') == null) {
      errors.push(translate("Du måste välja ett servicepaket innan du fortsätter."));
    }

    const addonNames = new Set();
    this.element.querySelectorAll('input[type="radio"][name^="addon_"]').forEach((el) => addonNames.add(el.name));
    addonNames.forEach((name) => {
      if (this.element.querySelector(`input[name="${name}"]:checked`) == null) {
        errors.push(translate("Vänligen ange ja eller nej för samtliga tilläggstjänster."));
      }
    });

    this.element.querySelectorAll(".personal_number_input").forEach((el) => {
      if (this.isHidden(el)) return;
      const value = (el.value || "").trim();
      if (!this.personalNumberRegex().test(value)) {
        errors.push(translate("Vänligen ange ett giltigt personnummer (ÅÅMMDD-NNNN)."));
      }
    });

    this.element.querySelectorAll(".address_input").forEach((el) => {
      if (this.isHidden(el) || (el.value || "").trim() !== "") return;
      errors.push(translate("Vänligen fyll i adress."));
    });
    this.element.querySelectorAll(".city_input").forEach((el) => {
      if (this.isHidden(el) || (el.value || "").trim() !== "") return;
      errors.push(translate("Vänligen fyll i stad."));
    });
    this.element.querySelectorAll(".postal_code_input").forEach((el) => {
      if (this.isHidden(el)) return;
      if (!/^\d{3}\s?\d{2}$/.test((el.value || "").trim())) {
        errors.push(translate("Vänligen fyll i ett giltigt postnummer."));
      }
    });
    this.element.querySelectorAll(".country_code_input").forEach((el) => {
      if (this.isHidden(el)) return;
      if (!/^[A-Z]{2}$/.test((el.value || "").trim().toUpperCase())) {
        errors.push(translate("Vänligen fyll i en giltig landskod."));
      }
    });

    return errors;
  }

  isHidden(el) {
    return el.closest(".hidden") != null || el.offsetParent === null;
  }

  personalNumberRegex() {
    const locale = (typeof gon !== "undefined" && gon.locale) || "sv-SE";
    return locale === "sv-SE" ? /^[0-9]{6,8}-?[0-9]{4}$/ : /^[0-9\- ]{6,}$/;
  }

  showError(message) {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.classList.remove("hidden");
    this.errorBannerTarget.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  hideError() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.classList.add("hidden");
  }

  startVerification() {
    $.ajax({
      url: this.verifyUrlValue,
      type: "GET",
      success: () => {},
      error: () => this.onVerifyFailed(),
    });
  }

  onVerifySuccess() {
    this.bypassValidation = true;
    this.element.querySelector("#step_3_form").requestSubmit();
  }

  onVerifyFailed() {
    this.spinnerTarget.classList.add("hidden");
    this.submitButtonTarget.classList.remove("hidden");
    const modal = document.getElementById("step_3_failed_job");
    if (modal) modal.classList.remove("hidden");
  }

  retry() {
    const modal = document.getElementById("step_3_failed_job");
    if (modal) modal.classList.add("hidden");
    this.submitButtonTarget.classList.add("hidden");
    this.spinnerTarget.classList.remove("hidden");
    $.ajax({
      url: this.verifyUrlValue,
      type: "GET",
      success: () => {},
      error: () => this.onVerifyFailed(),
    });
  }

  // Wired on the failed-job modal's own backdrop -- event.currentTarget is
  // that backdrop, event.target is whatever was actually clicked, equal
  // only when the click didn't bubble up from the card content inside it.
  closeFailedJobOnBackdrop(event) {
    if (event.target !== event.currentTarget) return;
    event.currentTarget.classList.add("hidden");
  }
}
